import React, { useState, useEffect } from "react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  TextField,
  Box,
} from "@mui/material";
import type { SurveyQuestion } from "posthog-js";

/**
 * Props for the SingleChoiceQuestion component.
 */
export interface SingleChoiceQuestionProps {
  /** The SurveyQuestion object defining the question and choices */
  question: SurveyQuestion;
  /** The currently selected choice value, or null if unanswered */
  value: string | null;
  /** Callback when the selection changes */
  onChange: (value: string) => void;
}

/**
 * Separator used between the "Other" choice label and custom text.
 * e.g., "Other: my custom answer"
 */
const OPEN_CHOICE_SEPARATOR = ": ";

/**
 * Parses the stored value to extract the selected choice and any custom text.
 * For open choice questions, the value format is "ChoiceLabel: customText".
 *
 * @param value - The stored response value
 * @param openChoiceLabel - The label of the open choice (e.g., "Other")
 * @returns Object containing selected choice and optional open text
 */
const parseOpenChoiceValue = (
  value: string | null,
  openChoiceLabel: string
): { selectedChoice: string; openText: string } => {
  if (!value) {
    return { selectedChoice: "", openText: "" };
  }

  // Check if this is an open choice response (starts with the open choice label + separator)
  const openChoicePrefix = `${openChoiceLabel}${OPEN_CHOICE_SEPARATOR}`;
  if (value.startsWith(openChoicePrefix)) {
    return {
      selectedChoice: openChoiceLabel,
      openText: value.slice(openChoicePrefix.length),
    };
  }

  // Regular choice
  return { selectedChoice: value, openText: "" };
};

/**
 * SingleChoiceQuestion Component
 * Renders a survey question with a set of radio button choices.
 * Supports `hasOpenChoice` for adding a freeform text input to the last choice.
 */
const SingleChoiceQuestion: React.FC<SingleChoiceQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  // Check if question has open choice (freeform text for "Other")
  const hasOpenChoice =
    "hasOpenChoice" in question && question.hasOpenChoice === true;

  // Check if question has valid choices
  const hasChoices = "choices" in question && question.choices;
  const choices = hasChoices ? question.choices : [];

  // The open choice is always the last choice when hasOpenChoice is true
  const openChoiceLabel =
    hasOpenChoice && choices.length > 0 ? choices[choices.length - 1] : null;

  // Parse the current value to get selected choice and open text
  const { selectedChoice, openText: initialOpenText } = parseOpenChoiceValue(
    value,
    openChoiceLabel ?? ""
  );

  // Local state for the open choice text input
  const [openText, setOpenText] = useState(initialOpenText);

  // Sync openText state when value prop changes (e.g., navigating back to question)
  useEffect(() => {
    const { openText: newOpenText } = parseOpenChoiceValue(
      value,
      openChoiceLabel ?? ""
    );
    setOpenText(newOpenText);
  }, [value, openChoiceLabel]);

  // Only proceed if the question has a valid "choices" array
  if (!hasChoices) {
    return null;
  }

  /**
   * Handles radio button selection change.
   * For the open choice, it reports the label + separator + any existing text.
   * For regular choices, it just reports the choice label.
   */
  const handleRadioChange = (newChoice: string) => {
    if (hasOpenChoice && newChoice === openChoiceLabel) {
      // When selecting the open choice, include any existing text
      if (openText.trim()) {
        onChange(`${openChoiceLabel}${OPEN_CHOICE_SEPARATOR}${openText}`);
      } else {
        // Report just the label to indicate selection (will be incomplete until text is entered)
        onChange(newChoice);
      }
    } else {
      onChange(newChoice);
    }
  };

  /**
   * Handles open choice text input change.
   * Updates the response to include the new text.
   */
  const handleOpenTextChange = (newText: string) => {
    setOpenText(newText);
    if (newText.trim()) {
      onChange(`${openChoiceLabel}${OPEN_CHOICE_SEPARATOR}${newText}`);
    } else {
      // If text is cleared, just report the label
      onChange(openChoiceLabel!);
    }
  };

  return (
    <RadioGroup
      value={selectedChoice}
      onChange={(e) => handleRadioChange(e.target.value)}
    >
      {choices.map((choice, index) => {
        const isOpenChoiceOption =
          hasOpenChoice && index === choices.length - 1;
        const isSelected = selectedChoice === choice;

        // For open choice option, render radio + label + inline text field
        if (isOpenChoiceOption) {
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                my: 0.5,
              }}
            >
              <FormControlLabel
                value={choice}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{choice}</Typography>}
                sx={{ my: 0, mr: 1 }}
              />
              {/* Show text input inline when open choice is selected */}
              {isSelected && (
                <TextField
                  size="small"
                  placeholder="Please specify..."
                  value={openText}
                  onChange={(e) => handleOpenTextChange(e.target.value)}
                  sx={{ flex: 1, minWidth: 150 }}
                  autoFocus
                />
              )}
            </Box>
          );
        }

        // Regular choice option
        return (
          <FormControlLabel
            key={index}
            value={choice}
            control={<Radio size="small" />}
            label={<Typography variant="body2">{choice}</Typography>}
            sx={{ my: 0.5 }}
          />
        );
      })}
    </RadioGroup>
  );
};

export default SingleChoiceQuestion;
